import { X } from "lucide-react"
import { useEffect, type ReactNode } from "react"
import { createPortal } from "react-dom"

type AudioComposeDialogProps = {
  title: string
  closeLabel: string
  busy?: boolean
  portalTarget: HTMLElement | null
  onClose: () => void
  /** false — клик по бэкдропу не закрывает модалку (запись и «Текст в речь»). */
  closeOnBackdrop?: boolean
  children: ReactNode
}

/** Модалка по центру переданной колонки: бэкдроп не закрывает остальной экран. */
export function AudioComposeDialog({
  title,
  closeLabel,
  busy = false,
  portalTarget,
  onClose,
  closeOnBackdrop = true,
  children,
}: AudioComposeDialogProps) {
  useEffect(() => {
    if (!portalTarget) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || busy) return
      const target = event.target instanceof Element ? event.target : null
      if (target?.closest("[role='listbox'], [role='menu']")) return
      event.preventDefault()
      onClose()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [busy, onClose, portalTarget])

  if (!portalTarget) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="absolute inset-0 z-40 flex items-center justify-center overflow-y-auto bg-[var(--ui-overlay-heavy)] p-4 backdrop-blur-sm"
      onPointerDown={(event) => {
        if (!closeOnBackdrop || busy || event.target !== event.currentTarget) return
        onClose()
      }}
    >
      <div
        className="flex w-full max-w-sm flex-col gap-3 rounded-lg border border-ui-border bg-ui-bg-elevated p-4 shadow-2xl shadow-black/50"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="min-w-0 truncate text-sm font-semibold text-ui-text-primary">{title}</h2>
          <button
            type="button"
            title={closeLabel}
            aria-label={closeLabel}
            disabled={busy}
            onClick={onClose}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ui-text-secondary transition-colors hover:bg-ui-surface-raised hover:text-ui-text-primary disabled:opacity-40"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    portalTarget,
  )
}

/** Поля внутри модалки: та же высота, что была у сегмента на дорожке. */
export const AUDIO_COMPOSE_FIELD =
  "box-border h-7 min-h-7 max-h-7 w-full rounded-md border border-ui-border bg-ui-bg px-2 text-xs leading-tight " +
  "text-ui-text-primary focus:border-ui-accent focus:outline-none focus:ring-0 " +
  "focus-visible:border-ui-accent focus-visible:ring-0 disabled:opacity-40"
